/**
 * Table of Contents Component
 * Dynamically generates a table of contents from article headings
 */
class TableOfContentsComponent {
  constructor(element) {
    this.element = element;
    this.targetSelector = element.getAttribute('data-target-selector');
    this.headingLevels = element.getAttribute('data-heading-levels').split(',');
    this.tocTitle = element.getAttribute('data-toc-title');
    this.autoHide = element.getAttribute('data-auto-hide') === 'true';
    
    this.tocContainer = element.querySelector('[data-toc-container]');
    this.tocList = element.querySelector('[data-toc-list]');
    
    this.init();
  }

  init() {
    this.generateTOC();
    this.bindEvents();
  }

  /**
   * Generate table of contents from article headings
   */
  generateTOC() {
    const targetElement = document.querySelector(this.targetSelector);
    if (!targetElement) {
      console.warn('TOC: Target element not found:', this.targetSelector);
      if (this.autoHide) {
        this.element.style.display = 'none';
      }
      return;
    }

    const headings = this.findHeadings(targetElement);
    console.log('TOC: Found headings:', headings.length, 'using selector:', this.headingLevels.join(','));
    
    if (headings.length === 0) {
      console.log('TOC: No headings found, hiding TOC');
      if (this.autoHide) {
        this.element.style.display = 'none';
      }
      return;
    }

    this.processHeadings(headings);
    const tocStructure = this.buildTOCStructure(headings);
    this.renderTOC(tocStructure);
    
    // Show TOC if it was hidden
    this.element.style.display = '';
    console.log('TOC: Generated successfully with', headings.length, 'headings');
  }

  /**
   * Find all headings in the target element
   */
  findHeadings(targetElement) {
    const headingSelector = this.headingLevels.join(',');
    const headings = targetElement.querySelectorAll(headingSelector);
    return Array.from(headings);
  }

  /**
   * Process headings by adding IDs and preparing data
   */
  processHeadings(headings) {
    const usedIds = new Set();
    
    // First pass: collect all existing IDs to avoid duplicates
    const allExistingIds = new Set();
    document.querySelectorAll('[id]').forEach(element => {
      if (element.id) {
        allExistingIds.add(element.id);
      }
    });
    
    headings.forEach((heading, index) => {
      // Generate unique ID if heading doesn't have one or if it's empty
      if (!heading.id || heading.id.trim() === '') {
        let baseId = this.generateSlug(heading.textContent);
        
        // Fallback if text content is empty or generates empty slug
        if (!baseId || baseId.trim() === '') {
          baseId = `heading-${index + 1}`;
        }
        
        let finalId = baseId;
        let counter = 1;
        
        // Ensure uniqueness against both existing IDs and previously assigned IDs
        while (usedIds.has(finalId) || allExistingIds.has(finalId)) {
          finalId = `${baseId}-${counter}`;
          counter++;
        }
        
        heading.id = finalId;
        usedIds.add(finalId);
        allExistingIds.add(finalId);
      } else {
        // Use existing ID but track it
        usedIds.add(heading.id);
      }
      
      // Store heading data for debugging and reference
      heading.dataset.tocLevel = heading.tagName.toLowerCase();
      heading.dataset.tocIndex = index;
      heading.dataset.tocProcessed = 'true';
    });
    
    // Log processed headings for debugging
    console.log('TOC: Processed headings:', headings.map(h => ({ 
      text: h.textContent.trim(), 
      id: h.id, 
      level: h.tagName 
    })));
  }

  /**
   * Generate URL-friendly slug from text
   */
  generateSlug(text) {
    if (!text || typeof text !== 'string') {
      return '';
    }
    
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
      .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
      .substring(0, 50); // Limit length to prevent overly long IDs
  }

  /**
   * Build hierarchical TOC structure
   */
  buildTOCStructure(headings) {
    const structure = [];
    const stack = [];
    
    headings.forEach(heading => {
      const level = parseInt(heading.tagName.charAt(1));
      const item = {
        id: heading.id,
        text: heading.textContent.trim(),
        level: level,
        children: []
      };
      
      // Find the correct parent level
      while (stack.length > 0 && stack[stack.length - 1].level >= level) {
        stack.pop();
      }
      
      if (stack.length === 0) {
        structure.push(item);
      } else {
        stack[stack.length - 1].children.push(item);
      }
      
      stack.push(item);
    });
    
    return structure;
  }

  /**
   * Render TOC HTML structure
   */
  renderTOC(structure) {
    this.tocList.innerHTML = this.renderTOCLevel(structure, 1);
  }

  /**
   * Render a specific level of the TOC
   */
  renderTOCLevel(items, level) {
    if (items.length === 0) return '';
    
    let html = '';
    let counter = 1;
    
    items.forEach(item => {
      const numbering = level === 1 ? `${counter}.` : '';
      const hasChildren = item.children.length > 0;
      
      html += `
        <li class="toc-item toc-item--level-${item.level}">
          <a href="#${item.id}" class="toc-link" data-toc-link="${item.id}">
            ${item.text}
          </a>
          ${hasChildren ? `<ol class="toc-sublist">${this.renderTOCLevel(item.children, level + 1)}</ol>` : ''}
        </li>
      `;
      
      counter++;
    });
    
    return html;
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Smooth scrolling for TOC links
    document.addEventListener('click', this.handleTOCLinkClick.bind(this));
    
    // Update active section on scroll
    window.addEventListener('scroll', this.throttle(this.updateActiveSection.bind(this), 100));
  }

  /**
   * Handle TOC link clicks
   */
  handleTOCLinkClick(event) {
    const link = event.target.closest('[data-toc-link]');
    if (!link || !this.element.contains(link)) return;
    
    event.preventDefault();
    
    const targetId = link.getAttribute('data-toc-link');
    const targetElement = document.getElementById(targetId);
    
    if (targetElement) {
      this.scrollToElement(targetElement);
      this.updateActiveLink(link);
    }
  }

  /**
   * Smooth scroll to element
   */
  scrollToElement(element) {
    const offsetTop = element.offsetTop + 600;

    console.log('TOC: Scrolling to element:', element.id, 'with offset:', offsetTop);
    
    window.scrollTo({
      top: offsetTop,
      behavior: 'smooth'
    });
  }

  /**
   * Update active section based on scroll position
   */
  updateActiveSection() {
    const headings = document.querySelectorAll(`${this.targetSelector} [id]`);
    const tocLinks = this.element.querySelectorAll('[data-toc-link]');
    
    let currentHeading = null;
    const scrollPosition = window.scrollY + 150; // Offset for better UX
    
    // Find the current section
    headings.forEach(heading => {
      if (heading.offsetTop <= scrollPosition) {
        currentHeading = heading;
      }
    });
    
    // Update active link
    tocLinks.forEach(link => {
      link.classList.remove('toc-link--active');
      if (currentHeading && link.getAttribute('data-toc-link') === currentHeading.id) {
        link.classList.add('toc-link--active');
      }
    });
  }

  /**
   * Update active link manually
   */
  updateActiveLink(activeLink) {
    const tocLinks = this.element.querySelectorAll('[data-toc-link]');
    tocLinks.forEach(link => link.classList.remove('toc-link--active'));
    activeLink.classList.add('toc-link--active');
  }

  /**
   * Throttle function for performance
   */
  throttle(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Auto-initialize table of contents components
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-component="table-of-contents"]').forEach(element => {
    new TableOfContentsComponent(element);
  });
}); 